import { useState } from "react";
import { Routes, Route, useLocation, NavLink, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { TaskEditorProvider } from "@/context/TaskEditorContext";
import { ChromeProvider, useChromeContext, useChromeHeader } from "@/context/ChromeContext";
import { appMode } from "@/lib/pocketbase";
import { useIsMobile } from "@/lib/useMediaQuery";
import { Sidebar } from "@/components/Sidebar";
import { TopNav } from "@/components/TopNav";
import { DemoBadge } from "@/components/DemoBadge";
import { GlobalSearch } from "@/components/GlobalSearch";
import { CalendarCheckIcon, TrayIcon, FolderIcon } from "@/components/icons";
import { useAllItems, useProjects } from "@/hooks/useData";
import { useBadgeTabs } from "@/lib/theme";
import { isOverdue, isToday, scheduledFor } from "@/lib/format";
import { itemProjectId } from "@/components/ItemRow";
import { Login } from "@/views/Login";
import { Projects } from "@/views/Projects";
import { Time } from "@/views/Time";
import { Clients } from "@/views/Clients";
import { Reports } from "@/views/Reports";
import { Expenses } from "@/views/Expenses";
import { ReportDetail } from "@/views/expenses/ReportDetail";
import { ProjectDetail } from "@/views/ProjectDetail";
import { Settings } from "@/views/Settings";
import { ListView } from "@/views/ListView";
import { Today } from "@/views/Today";
import { Roadmap } from "@/views/Roadmap";
import { Planner } from "@/views/Planner";
import { MobileShell } from "@/mobile/MobileShell";
import { SyncButton } from "@/components/SyncButton";

export default function App() {
  const { user } = useAuth();

  if (!user) return <Login />;

  return (
    <ChromeProvider>
      <TaskEditorProvider>
        <Shell />
      </TaskEditorProvider>
    </ChromeProvider>
  );
}

function Shell() {
  const mobile = useIsMobile();
  const [drawer, setDrawer] = useState(false);

  // The phone gets its own shell rather than another branch in this one. Below
  // this line, everything is the desktop layout and none of it needs to ask.
  if (mobile) return <MobileShell />;

  return (
    // The app bar runs across the top (the main pages, sync, account); below
    // it, the projects sidebar and the page, on the rail's colour.
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", background: "var(--bg2)" }}>
      <TopNav />
      <div style={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden", background: "var(--bg2)" }}>
        {!mobile && <Sidebar />}
        {mobile && (
          <>
            <div
              onClick={() => setDrawer(false)}
              style={{
                position: "fixed", inset: 0, background: "var(--scrim)", zIndex: 40,
                opacity: drawer ? 1 : 0, pointerEvents: drawer ? "auto" : "none", transition: "opacity 0.18s",
              }}
              className="no-print"
            />
            <div
              style={{
                position: "fixed", top: 0, bottom: 0, left: 0, zIndex: 41, width: 250,
                transform: drawer ? "translateX(0)" : "translateX(-100%)", transition: "transform 0.2s ease",
                boxShadow: drawer ? "var(--shadowOverlay)" : "none",
              }}
              className="no-print"
            >
              <Sidebar onNavigate={() => setDrawer(false)} />
            </div>
          </>
        )}

        <div
          style={{
            flex: 1, minWidth: 0, display: "flex", flexDirection: "column",
            // §6: the content pane has a top+left border, sitting on the
            // sidebar rail, with the folder tabs standing on its top edge.
            //
            // The pane is --bg, not --surface. Three steps, page-ward to
            // card-ward: rail --bg2, pane --bg, card --surface. When the pane was
            // white the cards inside it were the same white, so a card was a
            // hairline rectangle rather than something sitting on a surface.
            ...(mobile
              ? {}
              : {
                  background: "var(--bg)",
                  // The band the selected folder tab stands on (TopNav): the
                  // page's coloured edge, in the theme's accent.
                  borderTop: "4px solid var(--accent)",
                  borderLeft: "1px solid var(--line)",
                  // Square, not rounded: the first folder tab (Tasks) sits
                  // flush on this corner, and a curve here would clip it.
                  overflow: "hidden",
                }),
          }}
        >
          <AppHeader mobile={mobile} onMenu={() => setDrawer(true)} />
          <main style={{ flex: 1, minWidth: 0, overflowY: "auto" }}>
            <Routes>
              {/* Home is Today, the day view; the task list's own Today scope is /today. */}
              <Route path="/" element={<Today />} />
              <Route path="/today" element={<ListView preset="today" />} />
              <Route path="/week" element={<ListView preset="week" />} />
              <Route path="/next" element={<ListView preset="next" />} />
              <Route path="/overdue" element={<ListView preset="overdue" />} />
              <Route path="/all" element={<ListView preset="all" />} />
              <Route path="/upcoming" element={<ListView preset="upcoming" />} />
              {/* Calendar is a Work scope now; the old path still resolves. */}
              <Route path="/calendar" element={<ListView preset="calendar" />} />
              <Route path="/roadmap" element={<Roadmap />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/time" element={<Time />} />
              <Route path="/clients" element={<Clients />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/expenses" element={<Expenses />} />
              <Route path="/expenses/:id" element={<ReportDetail />} />
              <Route path="/planner" element={<Planner />} />
              {/* The weekly review is a Reports tab now; keep old links working. */}
              <Route path="/review" element={<Navigate to="/reports?tab=review" replace />} />
              <Route path="/project/:id" element={<ProjectDetail />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="*" element={<Today />} />
            </Routes>
          </main>
          {mobile && <BottomTabBar />}
        </div>
        {appMode === "demo" && <DemoBadge />}
      </div>
    </div>
  );
}

// The one persistent header (handoff §10b): back + title + subtitle on line one,
// global search on line two, view tabs on line three. Only the body swaps.
function AppHeader({ mobile, onMenu }: { mobile: boolean; onMenu: () => void }) {
  const { query, setQuery } = useChromeContext();
  const { title, subtitle, actions, tabs, back } = useChromeHeader();
  const loc = useLocation();
  const shownTitle = title || routeTitle(loc.pathname);

  if (mobile) {
    return (
      <header
        className="no-print"
        style={{
          display: "flex", flexDirection: "column", gap: 10, padding: "12px 16px 10px",
          borderBottom: "1px solid var(--line)", background: "var(--surface)", flex: "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={onMenu}
            aria-label="Open menu"
            style={{ width: 40, height: 40, flex: "none", borderRadius: 999, border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink2)", fontSize: 17, cursor: "pointer" }}
          >
            ☰
          </button>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 15.5, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{shownTitle}</div>
            {subtitle && <div style={{ fontSize: 11.5, color: "var(--ink3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subtitle}</div>}
          </div>
          {actions}
        </div>
        <GlobalSearch query={query} setQuery={setQuery} />
        {tabs && <div style={{ margin: "0 -16px", padding: "2px 16px 0", overflowX: "auto" }}>{tabs}</div>}
      </header>
    );
  }

  return (
    <header
      className="no-print"
      style={{
        padding: "18px 36px 0", borderBottom: "1px solid var(--line)", background: "var(--surface)",
        position: "sticky", top: 0, zIndex: 10, flex: "none",
      }}
    >
      {/* The quick log used to be a band here, styled like the page's own
          controls and mistaken for them. It lives behind "Log time" in the top
          bar now (TopNav), so everything in this header belongs to the page. */}
      {back && (
        <NavLink to={back.to} style={{ fontSize: 12.5, color: "var(--muted)", display: "inline-block", marginTop: 12 }}>‹ {back.label}</NavLink>
      )}

      {/* The title row does NOT wrap: a long project name should shorten, not
          push the page's actions onto a line of their own. The heading gets
          `flex: 1 1 auto; min-width: 0` so it is the thing that gives, and
          `nowrap` so its ellipsis actually fires — without min-width:0 a flex
          item refuses to shrink below its content and the ellipsis never
          engages. The full name stays available on hover. */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 20, marginTop: back ? 6 : 12 }}>
        <div style={{ flex: "1 1 260px", minWidth: 0 }}>
          <h1
            title={shownTitle}
            style={{
              margin: 0, fontSize: "var(--fs-h1)", fontWeight: "var(--fw-title)",
              letterSpacing: "var(--ls-title)",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}
          >{shownTitle}</h1>
          {subtitle && (
            <div style={{ fontSize: 12.5, color: "var(--ink3)", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {subtitle}
            </div>
          )}
        </div>
        {/* Search and sync sit with the page's own actions rather than on the
            quick-log band. They are not logging controls, and at 1120px they
            took 260px of the band, forcing the log onto a second row on every
            page. The title truncates, so this row absorbs them. */}
        <div style={{ display: "flex", alignItems: "center", gap: 9, flex: "0 1 auto", minWidth: 0, justifyContent: "flex-end" }}>
          {/* The page's own actions never compress — a progress capsule that
              wraps "8.00 h" onto two lines is worse than a shorter search box.
              Search is the only thing here that gives. */}
          <span style={{ display: "flex", alignItems: "center", gap: 9, flex: "none", whiteSpace: "nowrap" }}>
            {actions}
          </span>
          <GlobalSearch query={query} setQuery={setQuery} />
        </div>
      </div>
      {tabs ? <div style={{ marginTop: 10, paddingBottom: 10 }}>{tabs}</div> : <div style={{ height: 12 }} />}
    </header>
  );
}

// §7.5: the account avatar belongs top-right in the header, even single-user.


// Bottom tab bar for the four primary destinations (handoff §6.1). The drawer
// (via the header menu button) still holds the long tail.
const TABS: { to: string; label: string; Icon: typeof CalendarCheckIcon; end?: boolean }[] = [
  { to: "/", label: "Today", Icon: CalendarCheckIcon, end: true },
  { to: "/projects", label: "Projects", Icon: FolderIcon },
];

function BottomTabBar() {
  const { data: items = [] } = useAllItems();
  const { data: projects = [] } = useProjects();
  const [badges] = useBadgeTabs();

  const open = items.filter((i) => i.status !== "done");
  const counts: Record<string, number> = {
    "/": open.filter((i) => isOverdue(i) || isToday(scheduledFor(i))).length,
  };

  return (
    <nav
      className="no-print"
      style={{
        display: "flex", flex: "none", borderTop: "1px solid var(--line)", background: "var(--surface)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {TABS.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end={t.end}
          style={({ isActive }) => ({
            flex: 1, minHeight: 58, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4,
            textDecoration: "none", fontSize: 12,
            color: isActive ? "var(--accent)" : "var(--muted)", fontWeight: isActive ? 500 : 400,
          })}
        >
          {({ isActive }) => (
            <>
              <span style={{ position: "relative", display: "inline-flex" }}>
                {/* Colour and label weight carry the active state; the stroke
                    stays 1.5 so a selected tab is not a different icon set
                    from the ones beside it. */}
                <t.Icon size={20} />
                {badges === "on" && counts[t.to] > 0 && (
                  <span
                    className="mono"
                    style={{
                      position: "absolute", top: -4, right: -8, minWidth: 16, height: 16,
                      padding: "0 4px", borderRadius: 999, background: "var(--bad)", color: "var(--onAccent)",
                      fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >{counts[t.to]}</span>
                )}
              </span>
              {t.label}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function routeTitle(path: string): string {
  if (path === "/") return "Today";
  if (path.startsWith("/today")) return "Today";
  if (path.startsWith("/week")) return "This week";
  if (path.startsWith("/next")) return "Next week";
  if (path.startsWith("/overdue")) return "Overdue";
  if (path.startsWith("/all")) return "All items";
  if (path.startsWith("/calendar")) return "Calendar";
  if (path.startsWith("/board")) return "Status board";
  if (path.startsWith("/roadmap")) return "Roadmap";
  if (path.startsWith("/projects") || path.startsWith("/project")) return "Projects";
  if (path.startsWith("/import")) return "Import";
  if (path.startsWith("/expenses")) return "Expenses";
  if (path.startsWith("/settings")) return "Settings";
  return "Project Hub";
}
